import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  RefreshCw, Server, Play, Pause, Trash2, AlertTriangle, 
  CheckCircle, XCircle, Clock, Terminal, Database, Cpu,
  StopCircle, RotateCcw, Activity, Loader2, ChevronDown, ChevronUp,
  Download, Upload, HardDrive, FileArchive, AlertCircle, Settings, Zap, Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminServiceDashboard } from './AdminServiceDashboard';
import { AdminSettings } from './AdminSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== SYSTEM & SERVER TAB CONTENT ====================
function SystemServerContent() {
  const [systemStatus, setSystemStatus] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [logs, setLogs] = useState({ stdout: [], stderr: [] });
  const [selectedService, setSelectedService] = useState('backend');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showLogs, setShowLogs] = useState(true);
  const [showQueueLogs, setShowQueueLogs] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dbBackupLoading, setDbBackupLoading] = useState(false);
  const [dbRestoreLoading, setDbRestoreLoading] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  
  const logContainerRef = useRef(null);
  const queueLogRef = useRef(null);
  const fileInputRef = useRef(null);
  const dbFileInputRef = useRef(null);

  const fetchSystemStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/system/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch system status:', error);
    }
  }, []);

  const fetchQueueStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/spec-collection-queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueueStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/system/logs?service=${selectedService}&lines=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  }, [selectedService]);

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchSystemStatus(), fetchQueueStatus(), fetchLogs()]);
    setLoading(false);
  }, [fetchSystemStatus, fetchQueueStatus, fetchLogs]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchQueueStatus();
      if (showLogs) fetchLogs();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchQueueStatus, fetchLogs, showLogs]);

  // Auto-scroll logs
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  useEffect(() => {
    if (queueLogRef.current) {
      queueLogRef.current.scrollTop = queueLogRef.current.scrollHeight;
    }
  }, [queueStatus?.logs]);

  const handleAction = async (action, endpoint, method = 'POST') => {
    console.log(`[AdminSystem] Starting action: ${action}, endpoint: ${endpoint}`);
    setActionLoading(action);
    try {
      const token = localStorage.getItem('token');
      const response = await axios({
        method,
        url: `${API}${endpoint}`,
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log(`[AdminSystem] Action success:`, response.data);
      toast.success(`${action} erfolgreich`);
      
      // Refresh immediately and again after 2s
      await fetchQueueStatus();
      setTimeout(() => {
        fetchQueueStatus();
      }, 2000);
    } catch (error) {
      console.error(`[AdminSystem] Action failed:`, error);
      toast.error(`${action} fehlgeschlagen: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'RUNNING') return 'text-green-400';
    if (status === 'STOPPED') return 'text-red-400';
    return 'text-yellow-400';
  };

  const getStatusIcon = (status) => {
    if (status === 'RUNNING') return <CheckCircle className="w-4 h-4 text-green-400" />;
    if (status === 'STOPPED') return <XCircle className="w-4 h-4 text-red-400" />;
    return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
  };

  const getLogLevelColor = (level) => {
    switch (level) {
      case 'ERROR': return 'text-red-400';
      case 'WARNING': return 'text-yellow-400';
      case 'SUCCESS': return 'text-green-400';
      default: return 'text-gray-300';
    }
  };

  // Database Backup - Download
  const handleDatabaseBackup = async () => {
    setDbBackupLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use fetch for better blob handling
      const response = await fetch(`${API}/admin/database/backup`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}` 
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      
      // Get the blob
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Backup-Datei ist leer');
      }
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `carcortex_backup_${new Date().toISOString().split('T')[0]}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].trim().replace(/"/g, '');
      }
      
      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(`Backup heruntergeladen: ${filename} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
    } catch (error) {
      console.error('Backup failed:', error);
      toast.error(`Backup fehlgeschlagen: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setDbBackupLoading(false);
    }
  };

  // Database Restore - Upload
  const handleDatabaseRestore = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.zip')) {
      toast.error('Bitte wählen Sie eine ZIP-Datei aus');
      return;
    }

    setDbRestoreLoading(true);
    setRestoreResult(null);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/database/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      setRestoreResult(response.data);
      toast.success(`Datenbank wiederhergestellt: ${response.data.details?.total_documents || 0} Dokumente`);
      
      // Refresh all data after restore
      setTimeout(() => {
        fetchAll();
      }, 1000);
      
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error(`Restore fehlgeschlagen: ${error.response?.data?.detail || error.message}`);
      setRestoreResult({ status: 'error', message: error.response?.data?.detail || error.message });
    } finally {
      setDbRestoreLoading(false);
      setUploadProgress(null);
      // Reset file input
      if (dbFileInputRef.current) {
        dbFileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Auto-Refresh */}
      <div className="flex items-center justify-end gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="rounded border-gray-600"
          />
          Auto-Refresh
        </label>
        <Button
          onClick={fetchAll}
          variant="outline"
          size="sm"
          className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* System Actions */}
      <Card className="bg-[#0F141A] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Cpu className="w-5 h-5 text-cyan-400" />
              Server-Aktionen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button
                onClick={() => handleAction('Backend Neustart', '/admin/system/restart')}
                disabled={actionLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {actionLoading === 'Backend Neustart' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Backend Neustart
              </Button>
              
              <Button
                onClick={() => handleAction('Frontend Neustart', '/admin/system/restart-frontend')}
                disabled={actionLoading}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {actionLoading === 'Frontend Neustart' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Frontend Neustart
              </Button>
              
              <Button
                onClick={() => handleAction('Komplett-Neustart', '/admin/system/restart-all')}
                disabled={actionLoading}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                {actionLoading === 'Komplett-Neustart' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Alles Neustarten
              </Button>
              
              <Button
                onClick={() => handleAction('Hintergrundprozesse stoppen', '/admin/system/stop-background-tasks')}
                disabled={actionLoading}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
              >
                {actionLoading === 'Hintergrundprozesse stoppen' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <StopCircle className="w-4 h-4 mr-2" />
                )}
                Tasks Stoppen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Database Backup & Restore */}
        <Card className="bg-[#0F141A] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-emerald-400" />
              Datenbank-Verwaltung
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Backup Download */}
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Download className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">Backup herunterladen</span>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  Exportiert die gesamte Datenbank als ZIP-Archiv mit allen Collections.
                </p>
                <Button
                  onClick={handleDatabaseBackup}
                  disabled={dbBackupLoading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="db-backup-download-btn"
                >
                  {dbBackupLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Erstelle Backup...
                    </>
                  ) : (
                    <>
                      <FileArchive className="w-4 h-4 mr-2" />
                      Backup Herunterladen
                    </>
                  )}
                </Button>
              </div>

              {/* Restore Upload */}
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Upload className="w-5 h-5 text-amber-400" />
                  <span className="text-amber-400 font-medium">Backup wiederherstellen</span>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  Stellt die Datenbank aus einem zuvor erstellten ZIP-Backup wieder her.
                </p>
                
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={dbFileInputRef}
                  onChange={handleDatabaseRestore}
                  accept=".zip"
                  className="hidden"
                  data-testid="db-restore-file-input"
                />
                
                <Button
                  onClick={() => dbFileInputRef.current?.click()}
                  disabled={dbRestoreLoading}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="db-restore-upload-btn"
                >
                  {dbRestoreLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {uploadProgress !== null ? `Hochladen... ${uploadProgress}%` : 'Wiederherstellen...'}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      ZIP-Datei Hochladen
                    </>
                  )}
                </Button>
                
                {/* Upload Progress */}
                {uploadProgress !== null && (
                  <div className="mt-3">
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1 text-center">{uploadProgress}% hochgeladen</p>
                  </div>
                )}
              </div>
            </div>

            {/* Restore Result */}
            {restoreResult && (
              <div className={`p-4 rounded-lg ${
                restoreResult.status === 'success' 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {restoreResult.status === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-400" />
                  )}
                  <span className={`font-medium ${
                    restoreResult.status === 'success' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {restoreResult.status === 'success' ? 'Wiederherstellung erfolgreich' : 'Fehler'}
                  </span>
                </div>
                <p className="text-gray-300 text-sm">{restoreResult.message}</p>
                
                {restoreResult.details?.collections_restored && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs text-gray-400 font-medium">Wiederhergestellte Collections:</p>
                    {restoreResult.details.collections_restored.map((col, idx) => (
                      <div key={idx} className="flex justify-between text-xs text-gray-400">
                        <span>{col.name}</span>
                        <span>{col.documents_inserted} Dokumente</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {restoreResult.details?.errors?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-red-400 font-medium">Fehler:</p>
                    {restoreResult.details.errors.map((err, idx) => (
                      <p key={idx} className="text-xs text-red-300">{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Warning */}
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-300">
                <strong>Achtung:</strong> Das Wiederherstellen eines Backups überschreibt alle vorhandenen Daten. 
                Erstellen Sie vor dem Wiederherstellen immer erst ein aktuelles Backup!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Process Status */}
        <Card className="bg-[#0F141A] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-green-400" />
              Prozess-Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {systemStatus?.processes?.map((proc) => (
                <div
                  key={proc.name}
                  className="p-4 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(proc.status)}
                    <div>
                      <p className="font-medium text-white">{proc.name}</p>
                      <p className="text-xs text-gray-400">{proc.info}</p>
                    </div>
                  </div>
                  <span className={`text-sm font-mono ${getStatusColor(proc.status)}`}>
                    {proc.status}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Spec Collection Queue */}
        <Card className="bg-[#0F141A] border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-400" />
                Spec Collector Queue
                {queueStatus?.is_processing && (
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs animate-pulse">
                    Läuft
                  </span>
                )}
                {queueStatus?.is_paused && (
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs">
                    Pausiert
                  </span>
                )}
                {queueStatus?.watchdog_active && (
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-xs">
                    🐕 Watchdog
                  </span>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => handleAction('Alle Fahrzeuge einreihen', '/admin/spec-collection-queue/enqueue-all')}
                  disabled={actionLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {actionLoading === 'Alle Fahrzeuge einreihen' ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Database className="w-4 h-4 mr-1" />
                  )}
                  Alle einreihen
                </Button>
                {queueStatus?.is_paused ? (
                  <Button
                    size="sm"
                    onClick={() => handleAction('Queue fortsetzen', '/admin/spec-collection-queue/resume')}
                    disabled={!!actionLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {actionLoading === 'Queue fortsetzen' ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-1" />
                    )}
                    Fortsetzen
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleAction('Queue pausieren', '/admin/spec-collection-queue/pause')}
                    disabled={!!actionLoading}
                    variant="outline"
                    className="border-yellow-500/50 text-yellow-400"
                  >
                    {actionLoading === 'Queue pausieren' ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Pause className="w-4 h-4 mr-1" />
                    )}
                    Pausieren
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => handleAction('Queue starten', '/admin/spec-collection-queue/start')}
                  disabled={!!actionLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {actionLoading === 'Queue starten' ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-1" />
                  )}
                  Starten
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleAction('Queue leeren', '/admin/spec-collection-queue/clear', 'DELETE')}
                  disabled={!!actionLoading}
                  variant="destructive"
                >
                  {actionLoading === 'Queue leeren' ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Leeren
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Queue Stats */}
            <div className="grid grid-cols-5 gap-3">
              {[
                { label: 'Ausstehend', value: queueStatus?.counts?.pending || 0, color: 'text-blue-400' },
                { label: 'Verarbeitet', value: queueStatus?.counts?.processing || 0, color: 'text-yellow-400' },
                { label: 'Abgeschlossen', value: queueStatus?.counts?.completed || 0, color: 'text-green-400' },
                { label: 'Fehlgeschlagen', value: queueStatus?.counts?.failed || 0, color: 'text-red-400' },
                { label: 'Pausiert', value: queueStatus?.counts?.paused || 0, color: 'text-gray-400' }
              ].map((stat) => (
                <div key={stat.label} className="p-3 rounded-lg bg-white/5 text-center">
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Current Task */}
            {queueStatus?.current_task && (
              <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                  <span className="text-cyan-400 font-medium">Aktueller Task</span>
                </div>
                <p className="text-white font-mono">
                  {queueStatus.current_task.brand} {queueStatus.current_task.model}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Gestartet: {new Date(queueStatus.current_task.started_at).toLocaleTimeString('de-DE')}
                  {queueStatus.current_task.attempts > 0 && (
                    <span className="ml-2 text-yellow-400">
                      (Versuch {queueStatus.current_task.attempts + 1})
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Queue Log Console */}
            <div>
              <button
                onClick={() => setShowQueueLogs(!showQueueLogs)}
                className="flex items-center gap-2 text-gray-400 hover:text-white mb-2"
              >
                {showQueueLogs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                <Terminal className="w-4 h-4" />
                Queue-Konsole ({queueStatus?.logs?.length || 0} Einträge)
              </button>
              
              {showQueueLogs && (
                <div
                  ref={queueLogRef}
                  className="bg-black/50 rounded-lg p-4 h-64 overflow-y-auto font-mono text-xs"
                >
                  {queueStatus?.logs?.map((log, idx) => (
                    <div key={idx} className="flex gap-2 py-0.5">
                      <span className="text-gray-500 shrink-0">
                        {new Date(log.timestamp).toLocaleTimeString('de-DE')}
                      </span>
                      <span className={`shrink-0 w-16 ${getLogLevelColor(log.level)}`}>
                        [{log.level}]
                      </span>
                      <span className="text-gray-300">{log.message}</span>
                    </div>
                  ))}
                  {(!queueStatus?.logs || queueStatus.logs.length === 0) && (
                    <p className="text-gray-500">Keine Log-Einträge vorhanden</p>
                  )}
                </div>
              )}
            </div>

            {/* Settings */}
            <div className="p-3 rounded-lg bg-white/5 text-xs text-gray-400">
              <span className="font-medium text-gray-300">Einstellungen:</span>
              <span className="ml-2">Interval: {queueStatus?.settings?.interval_seconds}s</span>
              <span className="ml-3">Timeout: {queueStatus?.settings?.max_task_timeout_seconds}s</span>
              <span className="ml-3">Max Retries: {queueStatus?.settings?.max_retries}</span>
              <span className="ml-3">Watchdog: {queueStatus?.settings?.watchdog_interval_seconds}s</span>
            </div>
          </CardContent>
        </Card>

        {/* Server Logs */}
        <Card className="bg-[#0F141A] border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Terminal className="w-5 h-5 text-green-400" />
                Server-Logs
              </CardTitle>
              <div className="flex items-center gap-2">
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded px-3 py-1 text-white text-sm"
                >
                  <option value="backend">Backend</option>
                  <option value="frontend">Frontend</option>
                </select>
                <button
                  onClick={() => setShowLogs(!showLogs)}
                  className="text-gray-400 hover:text-white"
                >
                  {showLogs ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </CardHeader>
          {showLogs && (
            <CardContent>
              <div
                ref={logContainerRef}
                className="bg-black/50 rounded-lg p-4 h-80 overflow-y-auto font-mono text-xs"
              >
                {/* Stdout */}
                {logs.stdout?.map((line, idx) => (
                  <div key={`out-${idx}`} className="text-gray-300 py-0.5 whitespace-pre-wrap">
                    {line}
                  </div>
                ))}
                
                {/* Stderr */}
                {logs.stderr?.filter(l => l.trim()).length > 0 && (
                  <>
                    <div className="text-red-400 font-bold mt-4 mb-2">--- ERRORS ---</div>
                    {logs.stderr?.filter(l => l.trim()).map((line, idx) => (
                      <div key={`err-${idx}`} className="text-red-400 py-0.5 whitespace-pre-wrap">
                        {line}
                      </div>
                    ))}
                  </>
                )}
                
                {(!logs.stdout || logs.stdout.length === 0) && (
                  <p className="text-gray-500">Keine Logs vorhanden</p>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      </div>
  );
}

// ==================== VEHICLE IMAGES TAB CONTENT ====================
function VehicleImagesContent() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [batchSize, setBatchSize] = useState(20);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/vehicles/image-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Poll job status
  useEffect(() => {
    if (!jobStatus?.job_id || jobStatus?.status === 'completed') return;
    
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/admin/vehicles/image-generation-status/${jobStatus.job_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setJobStatus(response.data);
        
        if (response.data.status === 'completed') {
          toast.success(`Bildgenerierung abgeschlossen: ${response.data.result?.success || 0} erfolgreich`);
          fetchStats();
          setGenerating(false);
        }
      } catch (error) {
        console.error('Status check failed:', error);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [jobStatus?.job_id, jobStatus?.status]);

  const handleRepairPaths = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/vehicles/repair-images`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchStats();
    } catch (error) {
      toast.error('Reparatur fehlgeschlagen');
    }
  };

  const handleGenerateImages = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/vehicles/generate-missing-images?limit=${batchSize}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJobStatus(response.data);
      toast.success(`Generierung von ${response.data.generating} Bildern gestartet`);
    } catch (error) {
      toast.error('Start fehlgeschlagen');
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const missingCount = (stats?.with_invalid_path || 0) + (stats?.without_image || 0);
  const progress = jobStatus?.total ? 
    Math.round(((jobStatus.completed || 0) + (jobStatus.failed || 0)) / jobStatus.total * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Statistiken */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#0F141A] border-white/10">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-white">{stats?.total_vehicles || 0}</div>
            <div className="text-sm text-gray-400">Fahrzeuge gesamt</div>
          </CardContent>
        </Card>
        <Card className="bg-[#0F141A] border-green-500/30">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-green-400">{stats?.with_valid_image || 0}</div>
            <div className="text-sm text-gray-400">Mit Bild</div>
          </CardContent>
        </Card>
        <Card className="bg-[#0F141A] border-red-500/30">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-red-400">{missingCount}</div>
            <div className="text-sm text-gray-400">Ohne Bild</div>
          </CardContent>
        </Card>
        <Card className="bg-[#0F141A] border-amber-500/30">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-amber-400">
              {stats?.total_vehicles ? Math.round((stats.with_valid_image / stats.total_vehicles) * 100) : 0}%
            </div>
            <div className="text-sm text-gray-400">Abdeckung</div>
          </CardContent>
        </Card>
      </div>

      {/* Aktionen */}
      <Card className="bg-[#0F141A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-purple-400" />
            Bildgenerierung
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={handleRepairPaths}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Pfade reparieren
            </Button>
            
            <div className="flex items-center gap-2">
              <select
                value={batchSize}
                onChange={(e) => setBatchSize(Number(e.target.value))}
                className="bg-[#1a1f2e] border border-white/10 rounded px-3 py-2 text-white"
              >
                <option value={10}>10 Bilder</option>
                <option value={20}>20 Bilder</option>
                <option value={50}>50 Bilder</option>
                <option value={100}>100 Bilder</option>
              </select>
              
              <Button
                onClick={handleGenerateImages}
                disabled={generating || missingCount === 0}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generiere...
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Bilder generieren
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Progress */}
          {jobStatus && jobStatus.status === 'running' && (
            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-purple-400 font-medium">Generierung läuft...</span>
                <span className="text-gray-400 text-sm">
                  {(jobStatus.completed || 0) + (jobStatus.failed || 0)} / {jobStatus.total}
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-400">
                <span>✅ Erfolgreich: {jobStatus.completed || 0}</span>
                <span>❌ Fehlgeschlagen: {jobStatus.failed || 0}</span>
              </div>
            </div>
          )}

          {/* Kosten-Hinweis */}
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-300">
              <strong>Kosten:</strong> ~$0.04 pro Bild (OpenAI GPT Image). 
              {missingCount} fehlende Bilder ≈ ${(missingCount * 0.04).toFixed(2)}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Marken ohne Bilder */}
      {stats?.brands_missing_images?.length > 0 && (
        <Card className="bg-[#0F141A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg">Marken mit fehlenden Bildern</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {stats.brands_missing_images.map(([brand, count]) => (
                <div key={brand} className="p-2 rounded bg-red-500/10 border border-red-500/30">
                  <div className="text-white font-medium">{brand}</div>
                  <div className="text-red-400 text-sm">{count} fehlen</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== MAIN TABBED COMPONENT ====================
export default function AdminSystem() {
  const [activeTab, setActiveTab] = useState('server');

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Server className="w-7 h-7 text-cyan-400" />
            System-Verwaltung
          </h1>
          <p className="text-gray-400 mt-1">Server-Status, API-Services und Einstellungen</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#0F141A] border border-white/10 p-1 h-auto">
            <TabsTrigger 
              value="server" 
              className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 text-gray-400 px-4 py-2 flex items-center gap-2"
              data-testid="tab-server"
            >
              <Server className="w-4 h-4" />
              Server & Prozesse
            </TabsTrigger>
            <TabsTrigger 
              value="images" 
              className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 text-gray-400 px-4 py-2 flex items-center gap-2"
              data-testid="tab-images"
            >
              <ImageIcon className="w-4 h-4" />
              Fahrzeugbilder
            </TabsTrigger>
            <TabsTrigger 
              value="services" 
              className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-gray-400 px-4 py-2 flex items-center gap-2"
              data-testid="tab-services"
            >
              <Zap className="w-4 h-4" />
              API-Services
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 text-gray-400 px-4 py-2 flex items-center gap-2"
              data-testid="tab-settings"
            >
              <Settings className="w-4 h-4" />
              Einstellungen
            </TabsTrigger>
          </TabsList>

          <TabsContent value="server" className="mt-6">
            <SystemServerContent />
          </TabsContent>

          <TabsContent value="images" className="mt-6">
            <VehicleImagesContent />
          </TabsContent>

          <TabsContent value="services" className="mt-6">
            <AdminServiceDashboard />
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <AdminSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
